// Step 1: upload a DXF, map its layers to roles, build the project model and check it.
import { useState } from "react";
import { ROLE_LABELS, type LayerInventory, type LayerRole, type LengthUnit, type ProjectModel } from "@unitly/contracts";
import type { UploadProgress } from "../upload/uploader";
import { formatBytes } from "../upload/uploader";

export const ROLE_ORDER: LayerRole[] = [
  "apartment_boundary", "exterior_wall", "load_bearing", "movable_partition", "window",
  "entrance_door", "interior_door", "shaft", "room_label", "ignore",
];

export type Settings = { units: LengthUnit | null; north_angle_deg: number; gap_tolerance_m: number };

export type DrawingStage =
  | { kind: "empty" }
  | { kind: "uploading"; file: string; progress: UploadProgress | null }
  | { kind: "working"; what: "inventory" | "model"; progress: number | null; startedAt: number }
  | { kind: "layers" }
  | { kind: "model" };

export function DrawingPanel(props: {
  stage: DrawingStage;
  error: string | null;
  inventory: LayerInventory | null;
  model: ProjectModel | null;
  roles: Record<string, LayerRole>;
  setRole: (name: string, role: LayerRole) => void;
  hidden: Set<string>;
  toggleHidden: (name: string) => void;
  setHovered: (name: string | null) => void;
  hovered: string | null;
  settings: Settings;
  setSettings: (s: Settings) => void;
  onBuild: () => void;
  onEditLayers: () => void;
  onContinue: () => void;
  onCancelUpload: () => void;
  onRetryInventory: () => void;
}) {
  const { stage, inventory, model } = props;
  const title = stage.kind === "model" ? "Project model" : stage.kind === "layers" ? "Layers" : "Drawing";
  const sub =
    stage.kind === "model" ? `${model?.metadata.source_file.name ?? ""}` :
    stage.kind === "layers" ? `${inventory?.layers.length ?? 0} layers · ${inventory?.source.name ?? ""}` :
    stage.kind === "uploading" ? `Uploading ${stage.file}` :
    stage.kind === "working" ? (stage.what === "inventory" ? "Reading the drawing" : "Building the project model") :
    "No drawing loaded";

  return (
    <aside className="panel" aria-label="Drawing">
      <div className="panel-head">
        <span className="panel-title">{title}</span>
        <span className="panel-sub" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{sub}</span>
      </div>
      <div className="panel-body">
        {props.error && <div className="section"><div className="notice error" role="alert">{props.error}</div></div>}
        {stage.kind === "empty" && <EmptyHelp />}
        {stage.kind === "uploading" && <Uploading progress={stage.progress} />}
        {stage.kind === "working" && <Working what={stage.what} progress={stage.progress} startedAt={stage.startedAt} />}
        {stage.kind === "layers" && inventory && <Layers {...props} inventory={inventory} />}
        {stage.kind === "model" && model && <ModelSummary model={model} />}
      </div>
      <div className="panel-foot">
        {stage.kind === "uploading" && <button className="btn" onClick={props.onCancelUpload}>Cancel upload</button>}
        {stage.kind === "layers" && (
          <>
            <span className="faint" style={{ fontSize: 11.5 }}>{mappedHint(props.roles)}</span>
            <span className="spacer" />
            <button className="btn primary" disabled={!canBuild(props.roles)} onClick={props.onBuild}>
              Build project model<span className="unit" />
            </button>
          </>
        )}
        {stage.kind === "model" && (
          <>
            <button className="btn" onClick={props.onEditLayers}>Edit layer roles</button>
            <span className="spacer" />
            <button className="btn primary" onClick={props.onContinue}>Continue to parameters</button>
          </>
        )}
        {stage.kind === "empty" && props.error && inventory === null && (
          <button className="btn" onClick={props.onRetryInventory}>Try reading the drawing again</button>
        )}
      </div>
    </aside>
  );
}

function canBuild(roles: Record<string, LayerRole>) {
  const used = new Set(Object.values(roles));
  return used.has("apartment_boundary") || used.has("exterior_wall");
}

function mappedHint(roles: Record<string, LayerRole>) {
  if (!canBuild(roles)) return "Map the exterior walls or the apartment boundary first";
  const n = Object.values(roles).filter((r) => r !== "ignore").length;
  return `${n} layer${n === 1 ? "" : "s"} mapped`;
}

function EmptyHelp() {
  return (
    <div className="section" style={{ borderBottom: "none", fontSize: 12.5, lineHeight: 1.55 }}>
      <span>Load a floor plan to start.</span>
      <span className="muted">
        Unitly reads walls, columns, shafts, windows, doors and room labels from a DXF file, suggests a role for every
        layer, and builds a clean model of the apartment: the boundary, numbered windows on each facade, the entrance
        and the fixed elements the layout must keep.
      </span>
      <span className="faint">DWG is not supported yet. Save as DXF in AutoCAD (File, Save As, AutoCAD DXF).</span>
    </div>
  );
}

function Uploading({ progress }: { progress: UploadProgress | null }) {
  const pct = progress ? Math.floor((progress.sent / Math.max(1, progress.total)) * 100) : 0;
  const eta = progress && progress.bytesPerSecond > 0 ? Math.max(0, (progress.total - progress.sent) / progress.bytesPerSecond) : null;
  return (
    <div className="section" aria-live="polite">
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <span style={{ font: "600 22px var(--font-brand)" }}>{pct}%</span>
        <span className="muted">{progress ? `${formatBytes(progress.sent)} of ${formatBytes(progress.total)}` : "Starting"}</span>
      </div>
      <div className="progress" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}><div style={{ width: `${pct}%` }} /></div>
      {progress && (
        <span className="faint" style={{ fontSize: 12 }}>
          {formatBytes(progress.bytesPerSecond)}/s · part {progress.partsDone} of {progress.parts}
          {eta !== null && eta > 2 ? ` · about ${eta > 90 ? `${Math.round(eta / 60)} min` : `${Math.round(eta)} s`} left` : ""}
        </span>
      )}
      <span className="faint" style={{ fontSize: 12 }}>The file goes straight to storage in parts, so large drawings are fine.</span>
    </div>
  );
}

function Working({ what, progress, startedAt }: { what: "inventory" | "model"; progress: number | null; startedAt: number }) {
  const steps = what === "inventory"
    ? ["Starting the engine", "Downloading the drawing", "Reading layers and entities", "Suggesting roles"]
    : ["Starting the engine", "Downloading the drawing", "Repairing the boundary", "Snapping windows and doors", "Checking the model"];
  const p = progress ?? 0;
  const current = Math.min(steps.length - 1, Math.floor(p * steps.length));
  const secs = Math.round((Date.now() - startedAt) / 1000);
  return (
    <div className="section" aria-live="polite">
      <div className="progress"><div style={{ width: `${Math.max(4, p * 100)}%` }} /></div>
      {steps.map((s, i) => (
        <div key={s} style={{ display: "flex", gap: 10, fontSize: 12.5 }}>
          <span style={{ width: 12, color: i < current ? "var(--success)" : i === current ? "var(--unit-blue)" : "var(--n-400)" }}>
            {i < current ? "✓" : i === current ? "■" : "□"}
          </span>
          <span style={{ color: i <= current ? "var(--ink)" : "var(--n-400)" }}>{s}</span>
        </div>
      ))}
      <span className="faint" style={{ fontSize: 12 }}>
        {secs < 20 ? "The engine wakes up on demand; the first run can take a little longer." : `${secs} s so far. Large drawings take a minute or two.`}
      </span>
    </div>
  );
}

function Layers(props: Parameters<typeof DrawingPanel>[0] & { inventory: LayerInventory }) {
  const { inventory, roles, settings } = props;
  const [filter, setFilter] = useState("");
  const [showIgnored, setShowIgnored] = useState(true);
  const units = inventory.units;
  const rows = inventory.layers.filter((l) =>
    (showIgnored || roles[l.name] !== "ignore") && l.name.toLowerCase().includes(filter.toLowerCase()),
  );
  return (
    <>
      {(!units.plausible || !units.from_header) && units.hint && (
        <div className="section"><div className="notice warn">{units.hint}</div></div>
      )}
      <div className="section">
        <div style={{ display: "grid", gridTemplateColumns: "1.35fr 1fr 1fr", gap: 10 }}>
          <label className="field">Units
            <select className="select" value={settings.units ?? ""} onChange={(e) => props.setSettings({ ...settings, units: (e.target.value || null) as LengthUnit | null })}>
              <option value="">{`From file (${units.detected})`}</option>
              {(["mm", "cm", "m", "in", "ft"] as LengthUnit[]).map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </label>
          <label className="field">North angle
            <span className="unit-input">
              <input type="number" step={1} min={-360} max={360} value={settings.north_angle_deg}
                onChange={(e) => props.setSettings({ ...settings, north_angle_deg: clamp(Number(e.target.value), -360, 360) })} />
              <span>deg</span>
            </span>
          </label>
          <label className="field">Gap tolerance
            <span className="unit-input">
              <input type="number" step={5} min={0} max={500} value={Math.round(settings.gap_tolerance_m * 1000)}
                onChange={(e) => props.setSettings({ ...settings, gap_tolerance_m: clamp(Number(e.target.value), 0, 500) / 1000 })} />
              <span>mm</span>
            </span>
          </label>
        </div>
        <span className="faint" style={{ fontSize: 11.5 }}>North angle: where north points, clockwise from the top of the drawing. Gaps up to the tolerance are closed when the boundary is traced.</span>
      </div>
      <div className="section" style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
        <input className="input" placeholder="Filter layers" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter layers" />
        <button className="toggle" role="switch" aria-checked={showIgnored} onClick={() => setShowIgnored(!showIgnored)} style={{ whiteSpace: "nowrap", fontSize: 12.5 }}>
          <span className="track"><span className="knob" /></span>Ignored
        </button>
      </div>
      <div className="layer-row head" aria-hidden><span /><span>Layer</span><span style={{ textAlign: "right" }}>Items</span><span /><span>Role</span></div>
      <div role="list" onMouseLeave={() => props.setHovered(null)}>
        {rows.map((l) => {
          const role = roles[l.name] ?? "ignore";
          const auto = role === l.suggested_role && l.confidence !== "none";
          return (
            <div key={l.name} role="listitem"
              className={`layer-row${props.hovered === l.name ? " hover" : ""}${role === "ignore" ? " ignored" : ""}`}
              onMouseEnter={() => props.setHovered(l.name)} onFocus={() => props.setHovered(l.name)}
              title={Object.entries(l.entity_types).map(([t, n]) => `${t} ${n}`).join(", ")}>
              <span className="swatch" style={{ background: l.color_hex }} />
              <span className="name">
                <span>{l.name}</span>
                {auto && role !== "ignore" && <span className={`badge auto`} title={`Suggested from "${l.matched}"${l.confidence === "low" ? ", low confidence" : ""}`}>{l.confidence === "low" ? "AUTO?" : "AUTO"}</span>}
              </span>
              <span className="count">{l.entity_count.toLocaleString("en")}</span>
              <button className={`eye${props.hidden.has(l.name) ? " off" : ""}`} aria-label={props.hidden.has(l.name) ? `Show ${l.name}` : `Hide ${l.name}`}
                aria-pressed={!props.hidden.has(l.name)} onClick={() => props.toggleHidden(l.name)}><i /></button>
              <select className="select" aria-label={`Role of ${l.name}`} value={role} onChange={(e) => props.setRole(l.name, e.target.value as LayerRole)}>
                {ROLE_ORDER.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
            </div>
          );
        })}
        {!rows.length && <div className="section faint">No layers match.</div>}
      </div>
      <div className="section" style={{ borderBottom: "none" }}>
        <span className="faint" style={{ fontSize: 11.5 }}>
          {inventory.stats.entities_read.toLocaleString("en")} items read, {inventory.stats.entities_skipped.toLocaleString("en")} hatches,
          dimensions and other noise skipped in {inventory.stats.duration_s.toFixed(1)} s, peak memory {Math.round(inventory.stats.peak_memory_mb)} MB.
        </span>
      </div>
    </>
  );
}

function clamp(v: number, lo: number, hi: number) {
  return Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : lo;
}

function ModelSummary({ model }: { model: ProjectModel }) {
  const b = model.boundary;
  const xs = b.polygon.map((p) => p[0]), ys = b.polygon.map((p) => p[1]);
  const facades = [...new Set(model.openings.windows.map((w) => w.facade))].sort().join(", ") || "none";
  const columns = model.fixed.load_bearing.filter((x) => x.kind === "column").length;
  return (
    <>
      <div className="stats">
        <div><span className="eyebrow">Floor area</span><span className="v">{b.area_m2.toFixed(1)} m²</span></div>
        <div><span className="eyebrow">Outline</span><span className="v">{(Math.max(...xs) - Math.min(...xs)).toFixed(2)} × {(Math.max(...ys) - Math.min(...ys)).toFixed(2)} m</span></div>
        <div><span className="eyebrow">Windows</span><span className="v">{model.openings.windows.length} · {facades}</span></div>
        <div><span className="eyebrow">Fixed</span><span className="v">{columns} col · {model.fixed.shafts.length} shaft</span></div>
      </div>
      <div className="section" style={{ paddingBottom: 6 }}><span className="section-title">Validation</span></div>
      {model.validation.checks.map((c) => (
        <div key={c.id} className="check">
          <span className={`badge ${c.status}`}>{c.status.toUpperCase()}</span>
          <span>{c.message}</span>
        </div>
      ))}
      <div className="section" style={{ paddingBottom: 6 }}><span className="section-title">Windows <span className="hint">numbered counterclockwise</span></span></div>
      {model.openings.windows.map((w) => (
        <div key={w.id} className="check" style={{ gridTemplateColumns: "34px minmax(0,1fr) auto" }}>
          <span className="badge" style={{ background: "var(--unit-blue)", color: "#FFFFFF" }}>{w.id}</span>
          <span>{w.width_m.toFixed(2)} m · facade {w.facade} · edge {w.boundary_edge}</span>
          <span className="faint" style={{ fontSize: 11.5 }}>{w.source_layer}</span>
        </div>
      ))}
      {model.existing.room_labels.length > 0 && (
        <>
          <div className="section" style={{ paddingBottom: 6 }}><span className="section-title">Rooms read from the drawing</span></div>
          {model.existing.room_labels.map((l) => (
            <div key={l.id} className="check" style={{ gridTemplateColumns: "minmax(0,1fr) auto 56px" }}>
              <span>{l.text.split("  ")[0]}</span>
              <span className="faint">{l.suggested_type?.replace("_", " ") ?? ""}</span>
              <span style={{ textAlign: "right" }}>{l.parsed_area_m2?.toFixed(2) ?? ""}</span>
            </div>
          ))}
        </>
      )}
      <div className="section" style={{ borderBottom: "none" }}>
        <span className="faint" style={{ fontSize: 11.5 }}>
          Built in {model.metadata.processing.duration_s.toFixed(1)} s from {model.metadata.units.used}
          {model.metadata.units.overridden ? " (overridden)" : ""}; boundary from {b.source_layers?.join(", ") || "walls"}.
        </span>
      </div>
    </>
  );
}
